/**
 * React Query hooks for LLM Provider API operations.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as api from "@/api/client";
import type {
  LLMProvider,
  LLMProviderPresetsResponse,
  CreateLLMProviderInput,
  UpdateLLMProviderInput,
  CreateFromPresetInput,
  LLMProviderTestResult,
} from "@/api/types";

const PROVIDERS_KEY = ["llm-providers"];
const PRESETS_KEY = ["llm-providers", "presets"];

/**
 * Fetch all LLM providers.
 */
export function useLLMProviders() {
  return useQuery({
    queryKey: PROVIDERS_KEY,
    queryFn: async () => {
      const response = await api.get<{ providers: LLMProvider[] }>("/admin/llm-providers");
      return response.providers;
    },
  });
}

/**
 * Fetch a single LLM provider by ID.
 */
export function useLLMProvider(id: string | undefined) {
  return useQuery({
    queryKey: [...PROVIDERS_KEY, id],
    queryFn: () => api.get<LLMProvider>(`/admin/llm-providers/${id as string}`),
    enabled: !!id,
  });
}

/**
 * Fetch available presets and provider types.
 */
export function useLLMProviderPresets() {
  return useQuery({
    queryKey: PRESETS_KEY,
    queryFn: () => api.get<LLMProviderPresetsResponse>("/admin/llm-providers/presets"),
    staleTime: 60 * 60 * 1000, // 1 hour - presets don't change
  });
}

/**
 * Create a new LLM provider.
 */
export function useCreateLLMProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateLLMProviderInput) =>
      api.post<LLMProvider>("/admin/llm-providers", input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PROVIDERS_KEY });
    },
  });
}

/**
 * Create an LLM provider from a preset.
 */
export function useCreateLLMProviderFromPreset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ preset, ...input }: CreateFromPresetInput & { preset: string }) =>
      api.post<LLMProvider>(`/admin/llm-providers/from-preset/${preset}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PROVIDERS_KEY });
    },
  });
}

/**
 * Update an existing LLM provider.
 */
export function useUpdateLLMProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: UpdateLLMProviderInput & { id: string }) =>
      api.patch<LLMProvider>(`/admin/llm-providers/${id}`, input),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: PROVIDERS_KEY });
      queryClient.setQueryData([...PROVIDERS_KEY, data.id], data);
    },
  });
}

/**
 * Delete an LLM provider.
 */
export function useDeleteLLMProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.del(`/admin/llm-providers/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PROVIDERS_KEY });
    },
  });
}

/**
 * Test an LLM provider connection.
 */
export function useTestLLMProvider() {
  return useMutation({
    mutationFn: (id: string) =>
      api.post<LLMProviderTestResult>(`/admin/llm-providers/${id}/test`, {}),
  });
}
